import React from 'react';
import { Users } from 'lucide-react';
import { FilterMultiSelect, type FilterMultiSelectGroup } from '../FilterMultiSelect';
import type { EmployeeCompensationType } from '../../utils/payrollSummaryCalc';
import { normalizeCompensationType } from '../../utils/payrollSummaryCalc';

const AVATAR_COLORS = [
  { bg: 'bg-blue-100', text: 'text-blue-700' },
  { bg: 'bg-indigo-100', text: 'text-indigo-700' },
  { bg: 'bg-violet-100', text: 'text-violet-700' },
  { bg: 'bg-fuchsia-100', text: 'text-fuchsia-700' },
  { bg: 'bg-amber-100', text: 'text-amber-700' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700' },
];

export const EMPLOYEE_COMPENSATION_FILTER_GROUPS: FilterMultiSelectGroup[] = [
  { id: 'hourly', label: 'Lohnempfänger' },
  { id: 'salary', label: 'Gehaltsempfänger' },
];

export type EmployeeMultiSelectOption = {
  id: string;
  name: string;
  color_idx?: number;
  avatarColor?: string;
  initials?: string;
  subtitle?: string;
  /** Not available for the planned slot (badge + tooltip). */
  unavailable?: { label: string; detail: string };
  compensation_type?: EmployeeCompensationType | string | null;
};

export type EmployeeMultiSelectProps = {
  employees: EmployeeMultiSelectOption[];
  selected: string[];
  onChange: (ids: string[]) => void;
  selectionMode?: 'emptyMeansAll' | 'explicit';
  assignLabel?: string;
  countLabel?: string;
  allLabel?: string;
  searchPlaceholder?: string;
  emptyListLabel?: string;
  showSubtitle?: boolean;
  /** Default: Gruppen nach Lohn-/Gehaltsempfängern, wenn compensation_type vorhanden. */
  groupByCompensation?: boolean;
  icon?: React.ReactNode;
  className?: string;
  triggerClassName?: string;
  width?: number;
  disabled?: boolean;
  panelAlign?: 'start' | 'end';
  ariaLabel?: string;
};

export const EINSATZTEAM_MULTISELECT_LABELS = {
  assignLabel: 'Einsatzteam',
  countLabel: 'Einsatzteam',
  allLabel: 'Ganzes Einsatzteam',
  searchPlaceholder: 'Mitarbeiter suchen',
  emptyListLabel: 'Keine Mitarbeiter in Stammdaten',
  ariaLabel: 'Einsatzteam auswählen',
} as const;

/** @deprecated Alias für Lohnbuchhaltung / Übersicht. */
export type EmployeeOverviewFilterOption = EmployeeMultiSelectOption;

export {
  EMPLOYEE_MULTI_SELECT_NONE,
  FILTER_MULTI_SELECT_NONE,
  isEmployeeMultiSelectAll,
  isEmployeeMultiSelectNone,
  isFilterMultiSelectAll,
  isFilterMultiSelectNone,
} from '../filterMultiSelectUtils';

function avatarColorClass(colorIdx: number | undefined | null) {
  const idx = Number.isFinite(Number(colorIdx)) ? Number(colorIdx) : 0;
  return AVATAR_COLORS[((idx % AVATAR_COLORS.length) + AVATAR_COLORS.length) % AVATAR_COLORS.length];
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

function employeeLeading(emp: EmployeeMultiSelectOption): React.ReactNode {
  const initials = emp.initials ?? initialsFromName(emp.name);
  if (emp.avatarColor) {
    return (
      <span
        className="flex h-[22px] w-[22px] items-center justify-center rounded-full text-xs font-bold text-white"
        style={{ background: emp.avatarColor }}
      >
        {initials}
      </span>
    );
  }
  const avatar = avatarColorClass(emp.color_idx);
  return (
    <span
      className={`flex h-[22px] w-[22px] items-center justify-center rounded-full text-xs font-bold ${avatar.bg} ${avatar.text}`}
    >
      {initials}
    </span>
  );
}

function sortEmployeesForCompensationGroups(employees: EmployeeMultiSelectOption[]) {
  return [...employees].sort((a, b) => {
    const aType = normalizeCompensationType(a.compensation_type);
    const bType = normalizeCompensationType(b.compensation_type);
    if (aType !== bType) return aType === 'hourly' ? -1 : 1;
    return a.name.localeCompare(b.name, 'de');
  });
}

export function EmployeeMultiSelect({
  employees,
  selected,
  onChange,
  selectionMode = 'emptyMeansAll',
  assignLabel = 'Mitarbeiter',
  countLabel = 'Mitarbeiter',
  allLabel = 'Alle Mitarbeiter',
  searchPlaceholder = 'Mitarbeiter suchen',
  emptyListLabel = 'Keine Mitarbeiter',
  showSubtitle = false,
  groupByCompensation = false,
  icon = <Users className="h-3.5 w-3.5" aria-hidden />,
  className = '',
  triggerClassName = '',
  width = 320,
  disabled = false,
  panelAlign = 'end',
  ariaLabel = 'Mitarbeiter auswählen',
}: EmployeeMultiSelectProps) {
  const hasCompensationMeta = employees.some((e) => e.compensation_type != null);
  const useGroups = groupByCompensation && hasCompensationMeta;
  const ordered = useGroups ? sortEmployeesForCompensationGroups(employees) : employees;

  const options = ordered.map((emp) => ({
    id: emp.id,
    label: emp.name,
    subtitle: emp.subtitle,
    unavailable: emp.unavailable,
    leading: employeeLeading(emp),
    groupId: useGroups ? normalizeCompensationType(emp.compensation_type) : undefined,
  }));

  return (
    <FilterMultiSelect
      options={options}
      selected={selected}
      onChange={onChange}
      selectionMode={selectionMode}
      groups={useGroups ? EMPLOYEE_COMPENSATION_FILTER_GROUPS : undefined}
      allLabel={allLabel}
      countLabel={countLabel}
      assignLabel={assignLabel}
      searchPlaceholder={searchPlaceholder}
      emptyListLabel={emptyListLabel}
      showSubtitle={showSubtitle}
      icon={icon}
      className={className}
      triggerClassName={triggerClassName}
      width={width}
      disabled={disabled}
      panelAlign={panelAlign}
      ariaLabel={ariaLabel}
    />
  );
}

/** @deprecated Nutze EmployeeMultiSelect – Alias für bestehende Imports. */
export function EmployeeOverviewMultiSelect(props: EmployeeMultiSelectProps) {
  return <EmployeeMultiSelect {...props} selectionMode="emptyMeansAll" groupByCompensation />;
}
